import Locale from "./Locale";
import { dataSource } from '../Functions';
import { ControlConfig , ControlTypes } from '../config';
import { IconDefinition } from "../types";
import { safeJsonParse } from "./Json";
import { siteMode } from "../status";

/**
 * This function can be callled:
 * - for UI itmes to determine if it should be:
 * -- faded out
 * -- show a snowflake icon
 * - for the following buttons to determine if it can be pressable
 *   and if so, does a confirm popup needed to to be shown before execute the button actions:
 * -- power button
 * -- plus minus
 * All params are mandatory to pass in for determining state of buttons that would change controls.
 *
 * @returns E.g:
 * ```
 * {
 *   isPressable: boolean,
 *
 *   isFadedOut: boolean,
 *
 *   // pause FP, turn off blocker control, or none
 *   // Note: we might not need to return actionToOffer because the `reason` will tell us
 *   actionToOffer: string|undefined,
 *
 *   // helps to decide which popup to show
 *   // (e.g. can't use control because FP is running or can't go above 80 because shared restriction etc)
 *   reason: string,
 *
 *   // E.g: onoff, limit
 *   restrictionType: string|undefined,
 *
 *   restrictionValue: number|undefined,
 *
 *   // uuid of controls. can be passed to the function that will send RPC when user choose to proceeed
 *   blockerUuids: array|undefined
 * }
 * ```
 * @throws {Error}
 */
export function isPressable(
    control?: { UUID: string, Type: string },
    targetDesiredState?: {
        PowerLevel?: number,
        SetPoint?: number,
        Status?: string
    },
) {
    const isSiteOnlineAndReady = dataSource.isSiteOnline() && dataSource.isSiteReady();
    const isControllerReachable = dataSource.getIsServerReachable();
    const currentSiteMode = dataSource.getSiteMode();
    const isFreezeProtectOn = dataSource.getFreezeProtectState().On;
    const currentDesiredState = dataSource.getDesiredStates().find(item => item.ControlUUID === control.UUID);
    
    let selectedSite = dataSource.getSelectedSite();
    if (selectedSite.share && 
        (
            (selectedSite.share.from !== null && new Date(selectedSite.share.from).getTime() > Date.now()) || 
            (selectedSite.share.until !== null && new Date(selectedSite.share.until).getTime() < Date.now())
        )
    ) {
        // Share haven't started yet or expired
        return ({
            isPressable: false,
            isFadedOut: true,
            reason: 'share'
        });
    }

    let shareData = null;
    if (selectedSite.share?.data) {
        shareData = safeJsonParse(selectedSite.share.data);
    }

    // Checkings below are in the order of importance

    if (!isControllerReachable) return ({
        isPressable: false,
        isFadedOut: true, // we see tha last status...
        reason: 'noInternetConnection'
    });

    if (!isSiteOnlineAndReady) return ({
        isPressable: false,
        isFadedOut: true, // we see tha last status...
        reason: 'siteIsNotReady'
    });

    if (currentSiteMode !== siteMode.NORMAL) return ({
        isPressable: false,
        isFadedOut: true, // show the last desired states
        reason: 'sitemodeNotNormal'
    });

    if (currentDesiredState?.DisabledReasons?.includes('WINTERIZED')) return ({
        isPressable: false,
        isFadedOut: true, // it will alwas be grey anyways
        reason: 'winterized' // <--- this tells me to draw a snowflake icon
    });

    if (selectedSite.share?.level === 'audit' && shareData) {
        // Partial share.
        const share = shareData.controlOverrides?.find(item => item.controlUuid === control?.UUID);
        if (!share || !share.allowWrite) {
            // The control is not shared with the user
            return ({
                isPressable: false,
                isFadedOut: true,
                reason: 'share'
            });
        }

        switch (control?.Type) {
        case "Heating":
            if (typeof share.maxTemp !== 'undefined'
                && targetDesiredState?.SetPoint > share.maxTemp
            ) return ({
                isPressable: false,
                isFadedOut: false, // we dont want to fade out the button just becasue we are at the upper/lower limit. We dont do that in the numebr control even if it is not a shared poool
                reason: 'share',
                restrictionType: 'limit',
                restrictionValue: share.maxTemp
            });
            break;
        case "Cleaner":
        case "Filter":
        case "WaterFeature":
            if (typeof share.maxSpeed !== 'undefined'
                && targetDesiredState?.PowerLevel > share.maxSpeed
            ) return ({
                isPressable: false,
                isFadedOut: false, // we dont want to fade out the button just becasue we are at the upper/lower limit. We dont do that in the numebr control even if it is not a shared poool
                reason: 'share',
                restrictionType: 'limit',
                restrictionValue: share.maxSpeed
            });
            break;
        default:
        }
    }

    if (currentDesiredState?.DisabledReasons?.includes('FREEZE_PROTECT')) return ({
        isPressable: false,
        isFadedOut: true,
        actionToOffer: 'pauseFreezeProtect',
        reason: 'freezeprotect'
    });

    if (
        (
            isFreezeProtectOn
            && (
                !currentDesiredState?.DisabledReasons
                || currentDesiredState?.DisabledReasons.length === 0
            ) && currentDesiredState?.RestrictedMinimumPowerLevel > 0
        ) && (
            // User attempted to lower the power of the control than the restricted minimum
            (targetDesiredState?.PowerLevel < currentDesiredState.RestrictedMinimumPowerLevel)
            // User attempted to turn off the control
            || (targetDesiredState?.Status === 'OFF')
        )
    ) return ({
        isPressable: false,
        isFadedOut: false, // we accept theme button wont be greyed out
        actionToOffer: 'pauseFreezeProtect',
        reason: 'freezeprotect',
        restrictionType: 'limit',
        restrictionValue: currentDesiredState.RestrictedMinimumPowerLevel
    });

    if (currentDesiredState?.DisabledReasons?.length > 0) {
        if (currentDesiredState.Restricted) {
            if (targetDesiredState?.PowerLevel > currentDesiredState.RestrictedMaximumPowerLevel) return ({
                isPressable: false,
                isFadedOut: false, // we accept theme button wont be greyed out
                actionToOffer: 'turnOffAllBLockers',
                reason: 'blockers',
                restrictionType: 'limit',
                restrictionValue: currentDesiredState.RestrictedMaximumPowerLevel,
                blockerUuids: currentDesiredState.DisabledReasons
            });
        } else {
            const isCoverBlocker = currentDesiredState.DisabledReasons.some(
                (item: string) => dataSource.findEntity(item, 'Devices')?.entity?.DeviceType === 'Cover'
            );
            if (isCoverBlocker) {
                return ({
                    isPressable: false,
                    isFadedOut: true,
                    reason: 'poolcover',
                    restrictionType: 'onoff',
                    blockerUuids: currentDesiredState.DisabledReasons
                });
            } else {
                return ({
                    isPressable: false,
                    isFadedOut: true,
                    actionToOffer: 'turnOffAllBLockers',
                    reason: 'blockers',
                    restrictionType: 'onoff',
                    blockerUuids: currentDesiredState.DisabledReasons
                });
            }
        }
    }

    return ({
        isPressable: true,
        isFadedOut: false
    });

}

/**
 * Prepend BOW name to the control name in case there are multiple controls with the given name.
 * For heaters the control name is removed and only BOW name is added.
 * This is because there are a different logic for heaters in getTitle() and getBlockedDesiredStatesData().
 * There the words: "heating" and "heating up" is added.
 */
export function getControlName(
    control: { ControlGroupUUID: string, Type: string, Name: string },
    config: { ControlGroups: { UUID: string, Name: string }[], Controls: ControlConfig[]}
): string {
    if (!control) {
        console.warn('No control passed in');
        return 'unknown';
    }

    const controlGroup = config.ControlGroups.find((item) => item.UUID === control.ControlGroupUUID) || {
        UUID: control.ControlGroupUUID,
        Name: 'UUID: ' + control.ControlGroupUUID
    };
    if (control.Type === 'Heating') {
        return controlGroup?.Name;
    } else {
        const sameNameLength = config.Controls.filter(item => item.Name === control.Name)?.length ?? 0;
        if (sameNameLength > 1) {
            return controlGroup.Name + ' ' + control.Name;
        } else {
            return control.Name;
        }
    }
}

/**
 * ```
 * Can't <turn on/exceed> <Jet> <> <for 50%> because <Spa> <is turned on/is heating up>.
 * Can't <turn on/exceed> <Pool> <heating> <> because <Spa> <is turned on/is heating up>.
 * ```
 */ 
export function getTitle(restrictionType: string, restrictionValue: number, controlName: string, controlType: string, becauses: string[]): string {
    const verb = restrictionType === 'onoff' ? Locale.gettext('change') : Locale.gettext('exceed');
    const controlTypeText = controlType === 'Heating' ? ' ' + Locale.gettext('heating') : '';
    const limitText = restrictionType === 'onoff' ? '' : ' ' + Locale.gettext('for %s', restrictionValue);
    return Locale.gettext(
        'Can\'t %1$s %2$s%3$s%4$s because %5$s.',
        verb, controlName, controlTypeText, limitText,
        becauses.join(' ' + Locale.gettext('and') + ' ')
    );
}


/**
 * Returns a title and handler function to call for the given confirm button.
 * E.g. `{ title: "Pause freeze protect", action: async () => await pauseFreezeProtectDialog() }`
 */
export function getAction(areActionsDenied, offeredActions, blockerControls, pauseFreezeProtectDialog: Function, turnOffBlockers: Function, getControlNameW: Function): { title?: string, function?: Function } {
    if (areActionsDenied) {
        return {};
    }

    if (offeredActions.includes('pauseFreezeProtect')) {
        if (offeredActions.length === 1) return ({
            title: Locale.gettext('Pause Freeze Protect'),
            function: async () => {
                return await pauseFreezeProtectDialog();
            }
        });
        return ({
            title: Locale.gettext('Turn off blockers and pause FP'), // Longer text won't fit on the button
            function: async () => {
                await pauseFreezeProtectDialog();
                return await turnOffBlockers(offeredActions, blockerControls);
            }
        });
    } else {
        return ({
            title: Locale.gettext('Turn off %s',
                blockerControls.length > 1 ?
                    // won't fit in the button
                    // blockerControls.map(item => getControlName(item)).join(' ' + Locale.gettext('and') + ' ') :
                    Locale.gettext('all') : 
                    getControlNameW(blockerControls[0])
            ),
            function: async () => {
                return await turnOffBlockers(offeredActions, blockerControls);
            }
        });
    }
}

/**
 * Returns list of target desired states with descriptions why are they blocked.
 * Multiple target desired state can be passed in (usefule for checking blockers of a theme)
 * and each target desired state could have multiple blockers.
 * This function summarize them and collects all the actions that is offered to resolve the blocking.
 */
export function getBlockedDesiredStatesData(
    targetDesiredStates: { 
        ControlUUID: string,
        PowerLevel?: any,
        SetPoint?: any,
    }[],
    config: { Controls: any[], Devices: any[], ControlGroups: any[] },
    desiredStates: { ControlUUID: string, DisabledReasons?: string[] }[],
    getIcon?: (control: ControlConfig) => IconDefinition
) {
    const blockedDesiredStatesData = [];
    const blockerControls = [];

    const offeredActions = [];
    const blockerReasons = [];

    let areActionsDenied = false;

    targetDesiredStates.forEach((targetDS, idx) => {
        let title: string;
        let description: string;
        const titleParts = [];
        const descriptionParts = [];

        const control = config.Controls.find(item => item.UUID === targetDS.ControlUUID);
        if (!control) {
            console.warn('[ConfirmRestrictions] Target control does not exists. Control:', targetDS.ControlUUID);
            return;
        }

        const icon = getIcon && getIcon(control);
        let link: { url: string, text: string } | undefined;
        const currentDS = desiredStates.find((item) => item.ControlUUID === targetDS.ControlUUID) || {
            DisabledReasons: undefined
        };

        const isPressabeRes = isPressable(control, targetDS);

        if (isPressabeRes.actionToOffer) {
            if (!offeredActions.includes(isPressabeRes.actionToOffer)) {
                offeredActions.push(isPressabeRes.actionToOffer);
            }
        } else {
            areActionsDenied = true;
        }
        if (isPressabeRes.reason && !blockerReasons.includes(isPressabeRes.reason)) {
            blockerReasons.push(isPressabeRes.reason);
        }
        
        const controlName = getControlName(control, config);

        switch(isPressabeRes.reason) {
        // This shouldn't happen on Local UI because we don't even show the buttons when the controller is booting up
        case 'siteIsNotReady':
            titleParts.push(Locale.gettext("Not Ready"));
            descriptionParts.push(Locale.gettext(
                "You cannot use '%1$s' right now because the site is offline or is still starting up.  Please wait and try again later.",    
                controlName
            ));
            break;
        // This shouldn't happen on Local UI because it is independent from the internet connection
        case 'noInternetConnection':
            titleParts.push(Locale.gettext("No Internet Connection"));
            descriptionParts.push(Locale.gettext(
                "You cannot use '%1$s' right now because you are not connected to the cloud.  Please check your internet connection.",
                controlName
            ));
            link = { url: 'https://poolside.support/kb/checking-and-improving-your-attendants-internet-connection/', text: Locale.gettext('Troubleshooting') };
            break;
        // This shouldn't happen on Local UI because we don't even show the buttons when in IM
        case 'sitemodeNotNormal':
            titleParts.push(Locale.gettext(
                "%1$s Not Available",
                controlName
            ));
            descriptionParts.push(Locale.gettext(
                "You cannot use '%1$s' right now due to the current site mode.  Please try again later.",
                controlName
            ));
            break;
        case 'winterized':
            titleParts.push(Locale.gettext(
                "%1$s is Winterized",
                controlName
            ));
            descriptionParts.push(Locale.gettext(
                "You cannot use '%1$s' because it is winterized.",
                controlName
            ));
            link = { url: 'https://poolside.support/kb/closing-and-winterizing-your-pool/', text: Locale.gettext('More About Winterization') };
            break;
        // This shouldn't happen on Local UI share is not relevant to it
        case 'share':
            link = { url: 'https://poolside.support/kb/sharing/', text: Locale.gettext('Learn More About Permissions') };
            if (isPressabeRes.restrictionType === 'limit') {
                titleParts.push(Locale.gettext('Permissions Limit'));
                descriptionParts.push(Locale.gettext(
                    "You are only allowed to set '%1$s' to a maximum of %2$s.",    
                    controlName,
                    isPressabeRes.restrictionValue
                ));
                descriptionParts.push('\n\n' + Locale.gettext('Please contact the person who shared access of this site to you if you believe this is in error.'));
            } else {
                titleParts.push(Locale.gettext('Permissions Error'));
                descriptionParts.push(Locale.gettext(
                    "You are not able to control '%1$s' because your shared permission doesn\'t allow it.",
                    controlName
                ));
                descriptionParts.push('\n\n' + Locale.gettext('Please contact the person who shared access of this site to you if you believe this is in error.'));
            }
            break;
            case 'freezeprotect':
            titleParts.push(Locale.gettext("Freeze Protect is Running"));
            link = { url: 'https://poolside.support/kb/freeze-protection/', text: Locale.gettext('Learn More About Freeze Protection') };
            // If, while in complex FP, the user wishes to turn on the heater, we need to also explain that they can turn on Emergency Heating too
            if (control.Type === ControlTypes.Heating) {
                if (isPressabeRes.restrictionType === 'limit') {
                    descriptionParts.push(Locale.gettext(
                        "You are not able to turn off '%1$s' or lower the setting below %2$s because Freeze Protect is running.",
                        controlName,
                        isPressabeRes.restrictionValue
                    ));
                } else {
                    descriptionParts.push(Locale.gettext(
                        "Changes to '%1$s' are restricted because Freeze Protect is running.",
                        controlName
                    ));
                }
                descriptionParts.push("\n\n" + Locale.gettext("If you want to turn on heating to help prevent your plumbing from freezing, you should ensure that 'Emergency Heating' is enabled in Freeze Protection settings."));
                descriptionParts.push("\n\n" + Locale.gettext("If you want to turn on heating for another reason, such as using your hot tub, you can pause Freeze Protect below."));
                break;
            } else {
                if (isPressabeRes.restrictionType === 'limit') {
                    descriptionParts.push(Locale.gettext(
                        "You are not able to turn off '%1$s' or lower the setting below %2$s because Freeze Protect is running.  Once Freeze Protection has ended, you'll be able to make changes.",
                        controlName,
                        isPressabeRes.restrictionValue
                    ));
                } else {
                    descriptionParts.push(Locale.gettext(
                        "Changes to '%1$s' are restricted because Freeze Protect is running.  Once Freeze Protection has ended, you'll be able to make changes.",
                        controlName
                    ));
                }
                descriptionParts.push('\n\n' + Locale.gettext("You can optionally pause Freeze Protect if needed."));
                break;
            }
        case 'poolcover':
            titleParts.push(Locale.gettext("Pool Cover is On"));
            descriptionParts.push(Locale.gettext(
                "You cannot use '%1$s' right now because the pool cover is on.",
                controlName
            ));
            break;
        case 'blockers':
        default:
            const becauses = [];
            const toTurnOffs = [];
            currentDS.DisabledReasons?.forEach(disabledReason => {
                // disabledReason can be a UUID of a Control or a Device
                let blockerName = '';
                const blockerControl = config.Controls.find((ctrl) => ctrl.UUID === disabledReason);
                if (blockerControl) {
                    if (!blockerControls.some(ctrl => ctrl.UUID === blockerControl.UUID)) {
                        blockerControls.push(blockerControl);
                    };
                    blockerName = getControlName(blockerControl, config);
                } else {
                    const res = config.Devices.find(item => item.UUID === disabledReason);
                    if (res) {
                        blockerName = res.Name;
                    } else {
                        console.warn('[ConfirmRestrictions] Could not found the control or device:', disabledReason, 'included in DisabledReasons for currentDS:', currentDS)
                    }
                }

                // <Spa> <is heating up>
                // <Jet> <is turned on>
                becauses.push(Locale.gettext('%1$s %2$s',
                    blockerName,
                    blockerControl?.Type === 'Heating' ?
                        Locale.gettext('is heating up') :
                        Locale.gettext('is turned on')
                ));
                // <Spa> <heating>
                // <Jet> <>
                toTurnOffs.push(Locale.gettext('%1$s%2$s',
                    blockerName, 
                    blockerControl?.Type === 'Heating' ?
                        ' ' + Locale.gettext('heating') :
                        ''
                ));
            });

            titleParts.push(
                getTitle(
                    isPressabeRes.restrictionType, isPressabeRes.restrictionValue, controlName, control.Type, becauses
                )
            );
            descriptionParts.push(
                // We can turn off <Jet and Bubbles and Spa heating> if you are sure about it.
                Locale.gettext("You can turn off '%s' if you are sure about it.",
                    toTurnOffs.join(' ' + Locale.gettext('and') + ' ')
                )
            );
        }

        title = titleParts.join(' ');
        description = descriptionParts.join(' ');

        blockedDesiredStatesData.push({ title, description, icon, link });

    });

    return ({
        states: blockedDesiredStatesData,
        areActionsDenied,
        offeredActions,
        blockerControls
    });
}
